import {
    Asset,
    Color,
    createGraphicsDevice,
    Entity,
    Keyboard,
    Mouse,
    TouchDevice,
    type AppBase,
    type TextureHandler
} from 'playcanvas';

import { App } from '../app';
import { isApplePlatform } from '../platform';

export interface GsplatLoadOptions {
    /** URL to fetch from, or empty string when loading from contents buffer. */
    url: string;
    /** Filename hint for format detection by the asset handler. */
    filename: string;
    /** Raw ArrayBuffer; when provided the engine loads from memory instead of fetching url. */
    contents?: ArrayBuffer;
    /** Optional JSON metadata (e.g. parsed meta.json for .sog/.sogs). */
    metadata?: object;
    /** Enable anti-aliasing splat shader define. */
    aa: boolean;
}

/**
 * 创建 PlayCanvas App 及基础场景层级（cameraRoot / camera entity / light），
 * 供 standalone viewer（main）和 headless viewer 共用。
 * 注意：camera entity 此时尚未挂载 camera component，调用方负责按需添加。
 *
 * @param canvas - 用于创建图形设备的 HTML canvas 元素。
 * @param config - 创建配置。
 * @param config.renderer - 选择渲染后端：'webgl' 或 'webgpu'。
 * @returns 包含 App、camera entity 和实际渲染后端的对象。
 */
const createApp = async (canvas: HTMLCanvasElement, config: { renderer: 'webgl' | 'webgpu' }) => {
    const useWebGPU = config.renderer === 'webgpu';

    // Apple 平台（macOS / iOS / iPadOS）GPU 上开启 MSAA 会显著拉高带宽，
    // 导致 gsplat 渲染严重掉帧；其他平台仍开 MSAA 以保 Avatar 等 mesh 边缘质量。

    const device = await createGraphicsDevice(canvas, {
        deviceTypes: useWebGPU ? ['webgpu'] : [],
        antialias: !isApplePlatform(),
        depth: true,
        stencil: false,
        xrCompatible: true,
        powerPreference: 'high-performance'
    });

    console.log(`Renderer: ${device.deviceType}`);

    const renderer: 'webgl' | 'webgpu' = device.deviceType === 'webgpu' ? 'webgpu' : 'webgl';

    device.maxPixelRatio = window.devicePixelRatio;

    const app = new App(canvas, {
        graphicsDevice: device,
        mouse: new Mouse(canvas),
        touch: new TouchDevice(canvas),
        keyboard: new Keyboard(window)
    });

    (app.loader.getHandler('texture') as TextureHandler).imgParser.crossOrigin = 'anonymous';

    const cameraRoot = new Entity('camera root');
    app.root.addChild(cameraRoot);

    const camera = new Entity('camera');
    cameraRoot.addChild(camera);

    const light = new Entity('light');
    light.setEulerAngles(35, 45, 0);
    light.addComponent('light', {
        color: new Color(1.0, 0.98, 0.957),
        intensity: 1
    });
    app.root.addChild(light);

    app.scene.ambientLight.set(0.51, 0.55, 0.65);

    return { app, camera, renderer };
};

/**
 * 加载 gsplat 资产并挂载到场景根节点，返回承载 GSplatComponent 的 Entity。
 * 同时应用 180° Z 旋转（SuperSplat 坐标系约定）和 AA 着色器 define。
 *
 * @param app - 目标 PlayCanvas AppBase 实例。
 * @param opts - gsplat 加载选项（URL/文件名/内存数据/元数据/AA 开关）。
 * @param progressCallback - 加载进度回调，参数为 0~1 的进度值。
 * @returns 承载 GSplatComponent 的 Entity 的 Promise。
 */
const loadGsplat = (
    app: AppBase,
    opts: GsplatLoadOptions,
    progressCallback: (progress: number) => void
): Promise<Entity> => {
    const { url, filename, contents, metadata, aa } = opts;
    const asset = new Asset(filename, 'gsplat', { url, filename, contents: contents as any }, metadata);

    return new Promise<Entity>((resolve, reject) => {
        asset.on('load', () => {
            const entity = new Entity('gsplat');
            entity.setLocalEulerAngles(0, 0, 180);
            entity.addComponent('gsplat', {
                unified: true,
                asset
            });
            const material = app.scene.gsplat.material;
            material.setDefine('GSPLAT_AA', aa);
            material.setParameter('alphaClip', 1 / 255);
            app.root.addChild(entity);
            resolve(entity);
        });

        let watermark = 0;
        asset.on('progress', (received, length) => {
            const progress = Math.min(1, received / length) * 100;
            if (progress > watermark) {
                watermark = progress;
                progressCallback(Math.trunc(watermark));
            }
        });

        asset.on('error', (err) => {
            console.log(err);
            reject(err);
        });

        app.assets.add(asset);
        app.assets.load(asset);
    });
};

export { createApp, loadGsplat };
